import styled, { css } from 'styled-components';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';
import { DEEP_BORDER_BOX } from 'common';

const {
  SPACING,
  RADIUS,
  TYPOGRAPHY,
} = Tokens;

const MIN_HEIGHT = SPACING.SPACING_48;
const MIN_WIDTH = '6.25rem';
const ICON_SIZE = SPACING.SPACING_24;
const SPACING_1 = '0.0625rem';
const SPACING_6 = '0.375rem';
const SPACING_14 = '0.875rem';
const SPACING_50 = '3.125rem';

const TEXT_TRUNCATE_STYLE = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export const LIST_ITEM = css`
  ${props => css`direction: ${(props.RTL || props.$rtl || props.rtl) ? 'rtl' : 'ltr'};`}
  box-sizing: border-box;
  padding: ${SPACING.SPACING_4} ${SPACING.SPACING_8} ${SPACING.SPACING_4} ${SPACING.SPACING_16};
  ${({ RTL }) => RTL &&
    css`
    padding: ${SPACING.SPACING_4} ${SPACING.SPACING_16} ${SPACING.SPACING_4} ${SPACING.SPACING_8};
  `};
  background-color: ${({ $theme }) => $theme && $theme.background};
  color: ${({ $theme }) => $theme && $theme.text};
  display: flex;
  flex-direction: row;
  align-items:center;
  justify-content: flex-start;
  height: ${SPACING.SPACING_32};
  width: 100%;
  overflow: initial;
  outline: none;
  cursor: default;
  ${({ hoverEffect }) =>
    hoverEffect &&
    css`
    &:focus, &:focus-within {
      border: ${SPACING.SPACING_2} solid ${({ $theme }) => $theme && $theme.focused.border};
      background-color: ${({ $theme }) => $theme && $theme.focused.background};
      ${({ selected }) => selected && css` background-color: ${({ $theme }) => $theme && $theme.selected.focused.background};`}
      padding: ${SPACING.SPACING_2} ${SPACING_6} ${SPACING.SPACING_2} ${SPACING_14};
      ${({ RTL }) => RTL && css` padding: ${SPACING.SPACING_2} ${SPACING_14} ${SPACING.SPACING_2} ${SPACING_6};`}
    }
    ${({ selected }) => selected &&
    css`
      background-color:  ${({ $theme }) => $theme && $theme.selected.background};
      pointer-events: auto;
    `};
    &:hover {
      background-color: ${({ $theme }) => $theme && $theme.hovered.background};
      ${({ selected }) => selected && css` background-color: ${({ $theme }) => $theme && $theme.selected.hovered.background};`}
      cursor: pointer;
    }
  `};
  ${({ disabled }) =>
    disabled &&
  css`
    background-color: ${({ $theme }) => $theme && $theme.disabled.background};
    pointer-events: none;
  `};
  &:first-child:not(:focus):not(:focus-within) {
    ${props => css`border-top: ${props.hasBottomBorder ? `0.0625rem solid ${props.$theme.divider}` : 'none'};`} 
  }
  ${props => css`border-bottom: ${props.hasBottomBorder ? `0.0625rem solid ${props.$theme.divider}` : 'none'};`}
`;

export default {
  List: styled.div`
    ${DEEP_BORDER_BOX}
    ${props => css`direction: ${(props.RTL || props.$rtl || props.rtl) ? 'rtl' : 'ltr'};`}
    outline: none;
    max-width: 100%;
    width: 100%;
    max-height: 100%;
    min-width: ${MIN_WIDTH};
    min-height: ${MIN_HEIGHT};
    border-radius: ${RADIUS.RADIUS_2};
    padding: ${SPACING.SPACING_8} ${SPACING.SPACING_0};
    box-sizing: border-box;
    overflow: auto;
    ${props => css`overflow: ${props.isOverflowNecessary ? 'auto' : 'hidden'};`}
  `,
  ListUnOrdered: styled.ul`
    list-style-type: none;
    padding: 0;
    margin: 0;
    min-width: 100%;
    display: table;
  `,
  Divider: styled.li`
    background-color: ${({ $theme }) => $theme && $theme.divider};
    width: 100%;
    height: ${SPACING_1};
    margin-bottom: ${SPACING.SPACING_8};
    margin-top: ${SPACING.SPACING_8};
    list-style-type: none;
  `,
  ListItemActions: styled.div`
    display: flex;
    ${({ hide }) => hide && css`
      opacity: 0;
      width: 0;
      height: 0;
    `};
    > *:not(:first-child) {
      margin-left: ${props => (props.RTL || props.$rtl || props.rtl) ? Tokens.SPACING.SPACING_0 : Tokens.SPACING.SPACING_12};
      margin-right: ${props => (props.RTL || props.$rtl || props.rtl) ? Tokens.SPACING.SPACING_12 : Tokens.SPACING.SPACING_0};
    }
    ${props => css`direction: ${(props.RTL || props.$rtl || props.rtl) ? 'rtl' : 'ltr'};`}
    ${props => css`margin-right: ${(props.RTL || props.$rtl || props.rtl) ? 'auto' : `${SPACING.SPACING_8}`};`}
    ${props => css`margin-left: ${(props.RTL || props.$rtl || props.rtl) ? `${SPACING.SPACING_8}` : 'auto'};`}
  `,
  ListItemBasic: styled.li`
    ${LIST_ITEM}
  `,
  ListItemChecked: styled.li`
    ${LIST_ITEM}
    ${props => (props.RTL || props.$rtl || props.rtl) ? css`padding-left: ${SPACING.SPACING_24}; ` : css`padding-left: ${SPACING.SPACING_12}; `}
    &:focus {
      ${props => !props.RTL && css`padding-left: 0.625rem;`}
      ${props => props.RTL && css`padding-left: 1.375rem;`}
    }
`,
  ListItemCascading: styled.li`
    ${LIST_ITEM}
    ${({ cascadingListOpen }) =>
    cascadingListOpen &&
      css`
        background-color: ${({ $theme }) => $theme && $theme.selected.background} !important;
    `};
  `,
  CascadingIndicator: styled.div`
    pointer-events: none;
    margin-top: ${SPACING.SPACING_2};
    margin-left: auto;
    width: ${ICON_SIZE};
    height: ${ICON_SIZE};
    svg * {
      pointer-events: none;
      fill: ${({ $theme }) => $theme && $theme.icon};
    }
    ${({ disabled }) => disabled && css`
    svg {
      & * {
        fill: ${({ $theme }) => $theme && $theme.disabled.icon};
      }
    }
  `};
  `,
  CheckedIndicator: styled.div`
    pointer-events: none;
    margin-top: ${SPACING.SPACING_2};
    ${props => (props.RTL || props.$rtl || props.rtl) ? css`margin-left: ${SPACING.SPACING_12}; ` : css`margin-right: ${SPACING.SPACING_12}; `}
    width: ${ICON_SIZE};
    height: ${ICON_SIZE};
    svg * {
      pointer-events: none;
      fill: ${({ $theme }) => $theme && $theme.icon};
    }
    ${({ disabled }) => disabled && css`
    svg {
      & * {
        fill: ${({ $theme }) => $theme && $theme.disabled.icon};
      }
    }
  `};
  `,
  NoIndicator: styled.div`
    pointer-events: none;
    margin-top: ${SPACING.SPACING_2};
    margin-right: ${SPACING.SPACING_12};
    width: ${ICON_SIZE};
    height: ${ICON_SIZE};
  `,
  ListItemText: styled.div`
    user-select: none;
    ${({ $truncateListText }) => $truncateListText && css`
      ${TEXT_TRUNCATE_STYLE}
    `};
    ${({ hoverEffect }) =>
    hoverEffect &&
    css`
      user-select: text;
    `};
    color: ${({ $theme }) => $theme && $theme.text};
    white-space: nowrap;
    ${TYPOGRAPHY.BODY}
    ${props => css`direction: ${(props.RTL || props.$rtl || props.rtl) ? 'rtl' : 'ltr'};`}
    ${props => css`padding-right: ${(props.RTL || props.$rtl || props.rtl) ? '0' : `${SPACING.SPACING_12}`};`}
    ${({ disabled }) =>
    disabled &&
    css`
      color: ${({ $theme }) => $theme && $theme.disabled && $theme.disabled.text};
    `};
    width: 100%;
  `,
  ListItemIcon: styled.div`
    ${props => css`padding: ${(props.RTL || props.$rtl || props.rtl) ? `0 0 0 ${SPACING.SPACING_12}` : `0 ${SPACING.SPACING_12} 0 0`};`}
    ${({ disabled }) => disabled && css`
    svg {
      & * {
        fill: ${({ $theme }) => $theme && $theme.disabled && $theme.disabled.icon};
      }
    `};
    ${({ selected }) => selected && css`
    svg {
      & * {
        fill: ${({ $theme }) => $theme && $theme.icon};
      }
    `};
    svg {
      & * {
        fill:  ${({ $theme }) => $theme && $theme.icon};
    }
  `,
  ListGroupHeading: styled.li`
    ${({ stickyHeader }) =>
    stickyHeader && css`
      position: sticky;
      background-color: ${({ $theme }) => $theme && $theme.headerBackground};
      top: -${SPACING.SPACING_8};
      left: 0;
      padding: 0.4375rem ${SPACING.SPACING_8} 0.3125rem ${SPACING.SPACING_16};
    `};
    color:${({ $theme }) => $theme && $theme.groupHeading};
    ${({ stickyHeader }) => !stickyHeader && css`
      padding: 0.4375rem ${SPACING.SPACING_8} 0.3125rem ${SPACING.SPACING_16};
    `};
    ${({ RTL }) => RTL &&
      css`
      padding: 0.4375rem ${SPACING.SPACING_16} 0.3125rem ${SPACING.SPACING_8};
    `};
    text-transform: uppercase;
    height: 1.75rem;
    list-style-type: none;
    cursor: default;
    ${TYPOGRAPHY.LABEL}
  `,
  ListItemProgressIndicatorWrapper: styled.div`
    cursor: none;
    pointer-events: none;
    position: relative;
    padding-left: ${SPACING.SPACING_24};
    width: calc(100% - ${SPACING_50});
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  ListItemProgressIndicatorContent: styled.div`
    ${props => css`width: ${props.width ? props.width : '100%'};`}
    ${props => css`height: ${props.height ? props.height : '100%'};`}
  `,

  ListItemSubText: styled.div`
    ${TYPOGRAPHY.CAPTION};
    color: ${({ $theme }) => $theme && $theme.secondaryText};
    ${props => css`direction: ${(props.RTL || props.$rtl || props.rtl) ? 'rtl' : 'ltr'};`}
    ${props => css`padding-right: ${(props.RTL || props.$rtl || props.rtl) ? '0' : `${SPACING.SPACING_12}`};`}
    ${TEXT_TRUNCATE_STYLE};
    width: 100%;
    ${({ disabled }) => disabled &&
      css`
        color: ${({ $theme }) => $theme.disabled.text};
      `};
  `,

  ListItemHintText: styled.div`
    ${TYPOGRAPHY.CAPTION};
    color: ${({ $theme }) => $theme && $theme.hintText};
    ${props => css`direction: ${(props.RTL || props.$rtl || props.rtl) ? 'rtl' : 'ltr'};`}
    ${props => css`padding-right: ${(props.RTL || props.$rtl || props.rtl) ? '0' : `${SPACING.SPACING_12}`};`}
    ${TEXT_TRUNCATE_STYLE};
    overflow: visible;
    ${({ disabled }) => disabled &&
      css`
        color: ${({ $theme }) => $theme.disabled.text};
      `};
  `,

  ListItemTextContainer: styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start; 
    ${props => css`padding-right: ${(props.RTL || props.$rtl || props.rtl) ? `${SPACING.SPACING_16}` : `${SPACING.SPACING_12}`};`}
    ${TYPOGRAPHY.BODY}
    ${TEXT_TRUNCATE_STYLE}
  `,
};

